import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { PRIMARY, makeThemeColor, DEFAULT_MODE, TERTIARY } from '@tornado-rdk/theme';
import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { cacheResolver, makeDefaultStylingContext } from '../utils/style';
import { colors as progressColors, HEIGHT as PROGRESS_HEIGHT } from '../progress/progress.style';
import {
  TITLE_FONT_NAME,
  DISPLAY_ICONS,
  TITLE_FONT_TYPE,
  EXTRA_HIGHLIGHT_FONT_NAME,
  EXTRA_HIGHLIGHT_FONT_TYPE,
  templates,
  imageOptions,
  ICONS,
  SIDE_GLASS_ICONS,
} from './horizontalCard.constants';
import {
  BORDER_RADIUS,
  BORDER_WIDTH,
  INNER_BORDER_COLOR,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import { TRANSPARENT } from '../utils/colors';

export { PROGRESS_HEIGHT };

export const defaultContext = makeDefaultStylingContext();

export const IMAGE_OFFSET = {
  [HD]: BORDER_WIDTH[HD] * 2,
  [FULL_HD]: BORDER_WIDTH[FULL_HD] * 2,
};

export const TEXT_TOP_PADDING = {
  [HD]: 16,
  [FULL_HD]: 16,
};

export const TEXT_LEFT_PADDING = {
  [HD]: 24,
  [FULL_HD]: 24,
};

export const ICON_HEIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const LOCK_ICON_HEIGHT = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const ICON_PADDING_VERTICAL = 4;

export const ICON_PADDING_HORIZONTAL = 8;

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_HEIGHT[screenSize],
}));

export const getLockIconViewBox = _memoize(screenSize => ({
  h: LOCK_ICON_HEIGHT[screenSize],
}));

export const getCentralIconHeight = screenSize =>
  imageOptions[templates.HorizontalCard][RENDERING_CONTEXT.GENERAL][screenSize];

export const getCentralIconViewBox = _memoize(screenSize => ({
  h: getCentralIconHeight(screenSize).PRIMARY.height * 0.5,
  w: getCentralIconHeight(screenSize).PRIMARY.height * 0.5,
}));

export const colors = {
  text: makeThemeColor(PRIMARY),
  border: makeThemeColor(PRIMARY),
  innerBorder: INNER_BORDER_COLOR,
  overlayTop: makeThemeColor(TERTIARY, 0),
  overlayBottom: makeThemeColor(TERTIARY),
  gradient: makeThemeColor(TERTIARY),
};

export const makeBasicTextStyle = ({ font, colorMode = DEFAULT_MODE }) => ({
  color: colors.text[colorMode],
  text: {
    ...font,
    verticalAlign: 'middle',
    wordWrap: false,
    textOverflow: '...',
  },
});

export const makeBasicIconStyle = () => ({
  alpha: 0.8,
});

export const getIcon = (icons, position, sideGlass = false) => {
  const map = sideGlass ? SIDE_GLASS_ICONS : ICONS;

  return icons ? icons[map[position]] : null;
};

export const getInitialStyle = _memoize(({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => {
  const imageSize = imageOptions[templates.HorizontalCard][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY;
  const offset = IMAGE_OFFSET[screenSize];

  return {
    Border: {
      alpha: 0,
      w: imageSize.width + offset,
      h: imageSize.height + offset,
      x: -BORDER_WIDTH[screenSize],
      y: -BORDER_WIDTH[screenSize],
      shader: {
        outline: BORDER_WIDTH[screenSize],
        radius: BORDER_RADIUS[screenSize],
        strokeColor: TRANSPARENT,
      },
    },
    Image: {
      w: imageSize.width,
      h: imageSize.height,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        progressHeight: PROGRESS_HEIGHT,
        progressBackgroundColor: progressColors.background[colorMode],
        progressBarColor: progressColors.bar[colorMode],
      },
    },
    Gradient: {
      visible: false,
      // NOTE: compensating possible border erosion by the Lightning's Rounded Rectangle shader
      x: -1,
      y: -1,
      w: imageSize.width + 2,
      h: imageSize.height + 2,
      rect: true,
      color: colors.gradient[colorMode],
      alpha: 0.6,
      shader: {
        radius: BORDER_RADIUS[screenSize],
      },
    },
    ExtraFlexContainer: {
      visible: false,
      alpha: 1,
      w: imageSize.width,
      h: imageSize.height,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      ExtraHighlight: makeBasicTextStyle({
        font: getFont(EXTRA_HIGHLIGHT_FONT_NAME, screenSize, EXTRA_HIGHLIGHT_FONT_TYPE),
        colorMode,
      }),
    },
    TopLeftIcon: {
      x: ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    TopRightIcon: {
      ...makeBasicIconStyle(),
      visible: DISPLAY_ICONS,
      mountX: 1,
      x: imageSize.width - ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    BottomRightIcon: {
      mount: 1,
      x: imageSize.width - ICON_PADDING_HORIZONTAL,
      visible: DISPLAY_ICONS,
      alpha: 0.8,
    },
    FlexContainer: {
      x: imageSize.width + TEXT_LEFT_PADDING[screenSize],
      h: imageSize.height,
      Title: makeBasicTextStyle({
        font: getFont(TITLE_FONT_NAME, screenSize, TITLE_FONT_TYPE),
        colorMode,
      }),
      ExtraTitle: makeBasicTextStyle({
        font: getFont(FONT.BERLIN_1, screenSize),
        colorMode,
      }),
      Highlight: {
        ...makeBasicTextStyle({
          font: getFont(FONT.BERLIN_1, screenSize),
          colorMode,
        }),
        alpha: 0.6,
      },
      Description: {
        text: {
          ...getFont(FONT.BERLIN_1, screenSize),
          maxLines: 3,
          verticalAlign: 'middle',
          wordWrap: true,
          maxLinesSuffix: '...',
        },
      },
      Details: {
        ...makeBasicTextStyle({ font: getFont(FONT.BERLIN_1, screenSize), colorMode }),
        alpha: 0.6,
      },
    },
  };
}, cacheResolver);

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': colors.border[colorMode],
        'shader.innerColor': colors.innerBorder[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Image: {
      smooth: {
        scale: SCALE_FOCUSED,
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Gradient: {
      smooth: {
        scale: SCALE_FOCUSED,
      },
    },
    ExtraFlexContainer: {
      smooth: {
        alpha: 0,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
  Gradient: {
    smooth: {
      scale: 1,
    },
  },
  ExtraFlexContainer: {
    smooth: {
      alpha: 1,
    },
  },
};
